import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Serie } from "@nivo/line";

import { API_BASE_URL } from "../../../config";
import UsageLineChart from "../charts/UsageLineChart";
import ExportDialog, { FilterSummaryEntry } from "../export/ExportDialog";
import AutoCompleteMultiple from "../filters/AutoCompleteMultiple";
import { Checkbox, FormControlLabel, MenuItem, TextField } from "@mui/material";
import {
  buildLegendConfigs,
  getChartBottomMargin,
  resolveSeriesColors,
} from "../charts/chartUtils";
import FilterSection from "../filters/FilterSection";
import SubgroupSelector from "../filters/SubgroupSelector";
import {
  MAX_SELECTED_ANTIBIOTICS,
  MAX_SELECTED_ANIMALS,
  MAX_SELECTED_FORMS,
  MAX_SELECTED_ORDINATION_GROUPS,
  MAX_SELECTED_SUBGROUPS,
} from "../../constants";
import {
  arraysAreEqual,
  flattenAntibioticOptions,
  flattenSubgroupOptions,
  formatSubgroupLabel,
  getAntibioticDisplayLabel,
  getGroupLabelFromOption,
  isAntibioticGroupOption,
  sortSeriesByYear,
} from "../../utils/filterUtils";
import {
  AnimalSubgroupsResponse,
  AnimalsResponse,
  AntibioticsResponse,
  DashboardInstanceProps,
  OrdinationGroupsResponse,
  UsageResponse,
  VariableDimension,
  VariableOption,
} from "../../types";
import "../VetstatDashboard.scss";
import YearRangeSlider from "../../../common/YearRangeSlider";

const VARIABLE_OPTIONS: VariableOption[] = [
  {
    value: "all",
    label: "Antibiotics",
    dimension: "antibiotics",
    antibioticFilter: "all",
    helperText:
      "Choose freely between single antibiotics or entire antibiotic groups.",
  },
  {
    value: "animal_super",
    label: "Animal Species",
    dimension: "animals",
    helperText:
      "Compare animal species individually or combine them with locked antibiotics.",
  },
];

const DEFAULT_VARIABLE_BY_DIMENSION: Record<VariableDimension, string> = {
  antibiotics: "all",
  animals: "animal_super",
};

type DispensingLevel = "Dispensing1" | "Dispensing2";
const DISPENSING_LEVEL_OPTIONS: { label: string; value: DispensingLevel }[] = [
  { label: "Dispensing1", value: "Dispensing1" },
  { label: "Dispensing2", value: "Dispensing2" },
];

const getVariableOption = (value: string) =>
  VARIABLE_OPTIONS.find((option) => option.value === value);

const getOptionOrFallback = (
  value: string,
  fallbackDimension: VariableDimension
): VariableOption => {
  const found = getVariableOption(value);
  if (found) {
    return found;
  }
  const fallbackKey = DEFAULT_VARIABLE_BY_DIMENSION[fallbackDimension];
  return (
    getVariableOption(fallbackKey) ??
    VARIABLE_OPTIONS.find((option) => option.dimension === fallbackDimension) ??
    VARIABLE_OPTIONS[0]
  );
};

const DashboardInstance = ({
  instanceId,
}: DashboardInstanceProps) => {
  const [series, setSeries] = useState<Serie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [antibioticOptions, setAntibioticOptions] = useState<string[]>([]);
  const [selectedAntibiotics, setSelectedAntibiotics] = useState<string[]>([]);
  const [antibioticsLoading, setAntibioticsLoading] = useState(true);
  const [antibioticsError, setAntibioticsError] = useState<string | null>(null);
  const [animalOptions, setAnimalOptions] = useState<string[]>([]);
  const [selectedAnimals, setSelectedAnimals] = useState<string[]>([]);
  const [animalsLoading, setAnimalsLoading] = useState(true);
  const [animalsError, setAnimalsError] = useState<string | null>(null);
  const [formOptions, setFormOptions] = useState<string[]>([]);
  const [selectedForms, setSelectedForms] = useState<string[]>([]);
  const [formsLoading, setFormsLoading] = useState(true);
  const [formsError, setFormsError] = useState<string | null>(null);
  const [dispensingLevel, setDispensingLevel] = useState<DispensingLevel>(
    DISPENSING_LEVEL_OPTIONS[0].value
  );
  const [ordinationOptions, setOrdinationOptions] = useState<string[]>([]);
  const [selectedOrdinationGroups, setSelectedOrdinationGroups] = useState<
    string[]
  >([]);
  const [ordinationLoading, setOrdinationLoading] = useState(true);
  const [ordinationError, setOrdinationError] = useState<string | null>(
    null
  );
  const [animalSubgroupOptions, setAnimalSubgroupOptions] = useState<
    Record<string, string[]>
  >({});
  const [selectedAnimalSubgroups, setSelectedAnimalSubgroups] = useState<
    string[]
  >([]);
  const [animalSubgroupsLoading, setAnimalSubgroupsLoading] = useState(false);
  const [animalSubgroupsError, setAnimalSubgroupsError] = useState<
    string | null
  >(null);
  const [lockAntibiotics, setLockAntibiotics] = useState(true);
  const [lockAnimals, setLockAnimals] = useState(false);
  const [lockForms, setLockForms] = useState(false);
  const [lockOrdinationGroups, setLockOrdinationGroups] = useState(false);
  const [lockAnimalSubgroups, setLockAnimalSubgroups] = useState(false);

  const [minYear, setMinYear] = useState<number>(0);
  const [maxYear, setMaxYear] = useState<number>(0);
  const [selectedYearRange, setSelectedYearRange] = useState<number[]>([0, 0]);


  const baseURL = API_BASE_URL || "";
  const [chartReloading, setChartReloading] = useState(false);
  const chartReloadTimeoutRef = useRef<number | null>(null);
  const animalSubgroupsSelectId = `${instanceId}-animal-subgroups-select`;
  const [primaryVariableKey, setPrimaryVariableKey] = useState(
    DEFAULT_VARIABLE_BY_DIMENSION.antibiotics
  );
  const [secondaryVariableKey, setSecondaryVariableKey] = useState(
    DEFAULT_VARIABLE_BY_DIMENSION.animals
  );

  const primaryChoice = getOptionOrFallback(
    primaryVariableKey,
    "antibiotics"
  );
  const secondaryChoice = getOptionOrFallback(
    secondaryVariableKey,
    "animals"
  );
  const setLockStateForDimension = (
    dimension: VariableDimension,
    value: boolean
  ) => {
    if (dimension === "antibiotics") {
      setLockAntibiotics(value);
    } else {
      setLockAnimals(value);
    }
  };
  const getDimensionLockState = (dimension: VariableDimension) =>
    dimension === "antibiotics" ? lockAntibiotics : lockAnimals;
  const syncLockStateToDimension = (
    dimension: VariableDimension,
    value: boolean
  ) => {
    if (getDimensionLockState(dimension) !== value) {
      setLockStateForDimension(dimension, value);
    }
  };
  const dimensionUsageCount: Record<VariableDimension, number> = {
    antibiotics:
      (primaryChoice.dimension === "antibiotics" ? 1 : 0) +
      (secondaryChoice.dimension === "antibiotics" ? 1 : 0),
    animals:
      (primaryChoice.dimension === "animals" ? 1 : 0) +
      (secondaryChoice.dimension === "animals" ? 1 : 0),
  };

  const allowSubgroupSelection = selectedAnimals.length === 1;
  const primaryControlId = `${instanceId}-primary-select`;
  const secondaryControlId = `${instanceId}-secondary-select`;
  const antibioticLimit = lockAntibiotics ? MAX_SELECTED_ANTIBIOTICS : 1;
  const animalLimit = lockAnimals ? MAX_SELECTED_ANIMALS : 1;
  const formLimit = lockForms ? MAX_SELECTED_FORMS : 1;
  const ordinationLimit = lockOrdinationGroups ? MAX_SELECTED_ORDINATION_GROUPS : 1;
  const subgroupLimit = lockAnimalSubgroups ? MAX_SELECTED_SUBGROUPS : 1;
  const subgroupSelectionLimit = subgroupLimit;
  const resolvedColors = useMemo(
    () => resolveSeriesColors(series),
    [series]
  );
  const renderAntibioticOptionLabel = useCallback(
    (option: string) => (
      isAntibioticGroupOption(option) ? (
        <strong>{getGroupLabelFromOption(option)}</strong>
      ) : (
        option
      )
    ),
    []
  );
  const formatAntibioticTagLabel = useCallback(
    (option: string) => getAntibioticDisplayLabel(option),
    []
  );
  const antibioticOptionBuckets = useMemo(() => {
    const groupsOnly = antibioticOptions.filter((option) =>
      isAntibioticGroupOption(option)
    );
    const antibioticsOnly = antibioticOptions.filter(
      (option) => !isAntibioticGroupOption(option)
    );
    return {
      all: antibioticOptions,
      groups: groupsOnly,
      antibiotics: antibioticsOnly,
    };
  }, [antibioticOptions]);
  const getOptionsForChoice = useCallback(
    (choice: VariableOption) => {
      if (choice.dimension === "antibiotics") {
        const filterKey = choice.antibioticFilter ?? "all";
        return (
          antibioticOptionBuckets[filterKey] ?? antibioticOptionBuckets.all
        );
      }
      return animalOptions;
    },
    [antibioticOptionBuckets, animalOptions]
  );
  const primaryOptions = getOptionsForChoice(primaryChoice);
  const secondaryOptions = getOptionsForChoice(secondaryChoice);
  const activeAntibioticMeta =
    primaryChoice.dimension === "antibiotics"
      ? { choice: primaryChoice, options: primaryOptions }
      : secondaryChoice.dimension === "antibiotics"
      ? { choice: secondaryChoice, options: secondaryOptions }
      : null;
  const activeAntibioticChoice = activeAntibioticMeta?.choice ?? null;
  const activeAntibioticOptions = activeAntibioticMeta?.options;
  const primaryHelperText =
    primaryChoice.helperText ??
    "Adjust the selection to control which values are included in the graph.";
  const secondaryHelperText =
    secondaryChoice.helperText ??
    "Adjust the selection to control which values are included in the graph.";

  const usageParams = useMemo(() => {
    const params = new URLSearchParams();
    selectedAntibiotics.forEach((antibiotic) =>
      params.append("antibiotics", antibiotic)
    );
    selectedAnimals.forEach((animal) => params.append("animals", animal));
    selectedForms.forEach((form) =>
      params.append("dispenseringsform", form)
    );
    selectedOrdinationGroups.forEach((group) =>
      params.append("ordinationsgruppe", group)
    );
    params.append("dispensing_level", dispensingLevel);
    const activeSubgroups = allowSubgroupSelection
      ? selectedAnimalSubgroups
      : [];
    activeSubgroups.forEach((subgroup) =>
      params.append("animal_subgroups", subgroup)
    );
    if (selectedYearRange[0] !== 0) {
        params.append("from_year", selectedYearRange[0].toString());
        params.append("to_year", selectedYearRange[1].toString());
    }
    return params;
  }, [
    selectedAntibiotics,
    selectedAnimals,
    selectedForms,
    selectedOrdinationGroups,
    selectedAnimalSubgroups,
    allowSubgroupSelection,
    dispensingLevel,
    selectedYearRange,
  ]);
  const usageQuery = usageParams.toString();
  const startChartOverlay = () => {
    if (chartReloadTimeoutRef.current) {
      window.clearTimeout(chartReloadTimeoutRef.current);
      chartReloadTimeoutRef.current = null;
    }
    setChartReloading(true);
  };

  const stopChartOverlayWithDelay = () => {
    if (chartReloadTimeoutRef.current) {
      window.clearTimeout(chartReloadTimeoutRef.current);
    }
    chartReloadTimeoutRef.current = window.setTimeout(() => {
      setChartReloading(false);
      chartReloadTimeoutRef.current = null;
    }, 1000);
  };

  useEffect(
    () => () => {
      if (chartReloadTimeoutRef.current) {
        window.clearTimeout(chartReloadTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    fetch(baseURL.concat("/api/vetstat/vetstat_year_range"))
        .then(res => res.json())
        .then(res => {
            if (res.minYear && res.maxYear) {
                setMinYear(res.minYear);
                setMaxYear(res.maxYear);
                setSelectedYearRange([res.minYear, res.maxYear]);
            }
        })
        .catch(err => console.error("Failed to fetch year range", err));
  }, [baseURL]);

  useEffect(() => {
    const controller = new AbortController();
    setAntibioticsLoading(true);
    const params = new URLSearchParams();
    selectedAnimals.forEach((animal) => params.append("animals", animal));
    selectedForms.forEach((form) =>
      params.append("dispenseringsform", form)
    );
    selectedOrdinationGroups.forEach((group) =>
      params.append("ordinationsgruppe", group)
    );
    const activeSubgroups = allowSubgroupSelection
      ? selectedAnimalSubgroups
      : [];
    activeSubgroups.forEach((subgroup) =>
      params.append("animal_subgroups", subgroup)
    );
    const endpoint = baseURL.concat("/api/vetstat/antibiotics");
    const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint;

    fetch(url, {
      signal: controller.signal,
    })
      .then(async (res) => {
        const payload: AntibioticsResponse = await res.json();
        if (!res.ok) {
          throw new Error(
            payload?.detail ?? "Could not fetch list of antibiotics"
          );
        }
        return payload;
      })
      .then((result) => {
        const nextOptions = flattenAntibioticOptions(result.antibiotics);
        setAntibioticOptions(nextOptions);
        setAntibioticsError(null);
        setSelectedAntibiotics((current) => {
          const filtered = current
            .filter((antibiotic) => nextOptions.includes(antibiotic))
            .slice(0, antibioticLimit);
          return arraysAreEqual(filtered, current) ? current : filtered;
        });
      })
      .catch((err) => {
        if (err instanceof Error && err.name !== "AbortError") {
          setAntibioticsError(err.message);
        }
      })
      .finally(() => setAntibioticsLoading(false));

    return () => controller.abort();
  }, [
    baseURL,
    selectedAnimals,
    selectedForms,
    selectedOrdinationGroups,
    selectedAnimalSubgroups,
    antibioticLimit,
    allowSubgroupSelection,
  ]);
  useEffect(() => {
    if (!activeAntibioticChoice) {
      return;
    }
    const allowed = new Set(activeAntibioticOptions ?? []);
    setSelectedAntibiotics((current) => {
      const filtered = current
        .filter((value) => allowed.has(value))
        .slice(0, antibioticLimit);
      return arraysAreEqual(filtered, current) ? current : filtered;
    });
  }, [activeAntibioticChoice, activeAntibioticOptions, antibioticLimit]);

  useEffect(() => {
    setSelectedAntibiotics((current) => {
      if (current.length <= antibioticLimit) {
        return current;
      }
      return current.slice(0, antibioticLimit);
    });
  }, [antibioticLimit]);

  useEffect(() => {
    const controller = new AbortController();
    setAnimalsLoading(true);
    fetch(baseURL.concat("/api/vetstat/animals"), {
      signal: controller.signal,
    })
      .then(async (res) => {
        const payload: AnimalsResponse = await res.json();
        if (!res.ok) {
          throw new Error(
            payload?.detail ?? "Could not fetch list of animal species"
          );
        }
        return payload;
      })
      .then((result) => {
        setAnimalOptions(result.animals ?? []);
        setAnimalsError(null);
      })
      .catch((err) => {
        if (err instanceof Error && err.name !== "AbortError") {
          setAnimalsError(err.message);
        }
      })
      .finally(() => setAnimalsLoading(false));

    return () => controller.abort();
  }, [baseURL]);

  useEffect(() => {
    setSelectedAnimals((current) => current.slice(0, animalLimit));
  }, [animalLimit]);

  useEffect(() => {
    const controller = new AbortController();
    setFormsLoading(true);
    const params = new URLSearchParams();
    selectedAnimals.forEach((animal) => params.append("animals", animal));
    selectedAnimalSubgroups.forEach((subgroup) =>
      params.append("animal_subgroups", subgroup)
    );
    selectedOrdinationGroups.forEach((group) =>
      params.append("ordinationsgruppe", group)
    );
    params.append("dispensing_level", dispensingLevel);
    const endpoint = baseURL.concat("/api/vetstat/dispensing_forms");
    const url =
      params.toString() === "" ? endpoint : `${endpoint}?${params.toString()}`;

    fetch(url, {
      signal: controller.signal,
    })
      .then(async (res) => {
        const payload = await res.json();
        if (!res.ok) {
          throw new Error(
            payload?.detail ?? "Could not fetch list of dispensing forms"
          );
        }
        return payload;
      })
      .then((result) => {
        const nextOptions = result.forms ?? [];
        setFormOptions(nextOptions);
        setFormsError(null);
        setSelectedForms((current) => {
          const filtered = current.filter((value) => nextOptions.includes(value));
          return arraysAreEqual(filtered, current) ? current : filtered;
        });
      })
      .catch((err) => {
        if (err instanceof Error && err.name !== "AbortError") {
          setFormsError(err.message);
        }
      })
      .finally(() => setFormsLoading(false));

    return () => controller.abort();
  }, [
    baseURL,
    selectedAnimals,
    selectedAnimalSubgroups,
    selectedOrdinationGroups,
    dispensingLevel,
  ]);

  useEffect(() => {
    setSelectedForms((current) => {
      if (current.length <= formLimit) {
        return current;
      }
      return current.slice(0, formLimit);
    });
  }, [formLimit]);

  useEffect(() => {
    const controller = new AbortController();
    setOrdinationLoading(true);
    const params = new URLSearchParams();
    selectedAnimals.forEach((animal) => params.append("animals", animal));
    selectedAnimalSubgroups.forEach((subgroup) =>
      params.append("animal_subgroups", subgroup)
    );
    selectedForms.forEach((form) =>
      params.append("dispenseringsform", form)
    );
    selectedAntibiotics.forEach((antibiotic) =>
      params.append("antibiotics", antibiotic)
    );
    params.append("dispensing_level", dispensingLevel);
    const endpoint = baseURL.concat("/api/vetstat/ordination_groups");
    const url =
      params.toString() === "" ? endpoint : `${endpoint}?${params.toString()}`;

    fetch(url, {
      signal: controller.signal,
    })
      .then(async (res) => {
        const payload: OrdinationGroupsResponse = await res.json();
        if (!res.ok) {
          throw new Error(
            payload?.detail ??
              "Could not fetch list of ordination groups"
          );
        }
        return payload;
      })
      .then((result) => {
        const nextOptions = result.groups ?? [];
        setOrdinationOptions(nextOptions);
        setOrdinationError(null);
        setSelectedOrdinationGroups((current) => {
          const filtered = current.filter((value) => nextOptions.includes(value));
          return arraysAreEqual(filtered, current) ? current : filtered;
        });
      })
      .catch((err) => {
        if (err instanceof Error && err.name !== "AbortError") {
          setOrdinationError(err.message);
        }
      })
      .finally(() => setOrdinationLoading(false));

    return () => controller.abort();
  }, [
    baseURL,
    selectedAnimals,
    selectedAnimalSubgroups,
    selectedForms,
    selectedAntibiotics,
    dispensingLevel,
  ]);

  useEffect(() => {
    setSelectedOrdinationGroups((current) => {
      if (current.length <= ordinationLimit) {
        return current;
      }
      return current.slice(0, ordinationLimit);
    });
  }, [ordinationLimit]);

  useEffect(() => {
    if (!allowSubgroupSelection || selectedAnimals.length === 0) {
      setAnimalSubgroupOptions({});
      setSelectedAnimalSubgroups([]);
      setAnimalSubgroupsError(null);
      setAnimalSubgroupsLoading(false);
      return;
    }

    const controller = new AbortController();
    setAnimalSubgroupsLoading(true);
    const params = new URLSearchParams();
    selectedAnimals.forEach((animal) => params.append("animals", animal));
    selectedAntibiotics.forEach((antibiotic) =>
      params.append("antibiotics", antibiotic)
    );
    selectedForms.forEach((form) =>
      params.append("dispenseringsform", form)
    );
    selectedOrdinationGroups.forEach((group) =>
      params.append("ordinationsgruppe", group)
    );
    params.append("dispensing_level", dispensingLevel);
    fetch(
      `${baseURL.concat("/api/vetstat/animal_subgroups")}?${params.toString()}`,
      {
        signal: controller.signal,
      }
    )
      .then(async (res) => {
        const payload: AnimalSubgroupsResponse = await res.json();
        if (!res.ok) {
          throw new Error(
            payload?.detail ??
              "Could not fetch subgroups for the selected animal species"
          );
        }
        return payload;
      })
      .then((result) => {
        const nextOptions = result.subgroups ?? {};
        setAnimalSubgroupOptions(nextOptions);
        const allowedValues = new Set(flattenSubgroupOptions(nextOptions));
        setSelectedAnimalSubgroups((current) =>
          current.filter((value) => allowedValues.has(value))
        );
        setAnimalSubgroupsError(null);
      })
      .catch((err) => {
        if (err instanceof Error && err.name !== "AbortError") {
          setAnimalSubgroupsError(err.message);
        }
      })
      .finally(() => setAnimalSubgroupsLoading(false));

    return () => controller.abort();
  }, [
    baseURL,
    selectedAnimals,
    selectedAntibiotics,
    selectedForms,
    selectedOrdinationGroups,
    allowSubgroupSelection,
    dispensingLevel,
  ]);

  useEffect(() => {
    if (subgroupSelectionLimit <= 0) {
      setSelectedAnimalSubgroups([]);
      return;
    }
    setSelectedAnimalSubgroups((current) =>
      current.slice(0, subgroupSelectionLimit)
    );
  }, [subgroupSelectionLimit]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    startChartOverlay();
    const usageEndpoint = baseURL.concat("/api/vetstat/usage_by_year");
    const queryString = usageParams.toString();
    const urlWithParams = queryString
      ? `${usageEndpoint}?${queryString}`
      : usageEndpoint;

    fetch(urlWithParams, { signal: controller.signal })
      .then(async (res) => {
        const payload: UsageResponse = await res.json();
        if (!res.ok) {
          throw new Error(payload?.detail ?? "Could not fetch VetStat data");
        }
        return payload;
      })
      .then((result) => {
        const sortedSeries = sortSeriesByYear(result.series ?? []);
        setSeries(sortedSeries);
        setError(null);
      })
      .catch((err) => {
        if (err instanceof Error && err.name !== "AbortError") {
          setError(err.message);
        }
      })
      .finally(() => {
        setLoading(false);
        stopChartOverlayWithDelay();
      });

    return () => controller.abort();
  }, [baseURL, usageParams]);

  const hasData = useMemo(
    () => series.some((entry) => entry.data.length > 0),
    [series]
  );
  const filtersSummary = useMemo<FilterSummaryEntry[]>(() => {
    const subgroupValues =
      selectedAnimalSubgroups.length > 0
        ? selectedAnimalSubgroups.map((value) => formatSubgroupLabel(value))
        : allowSubgroupSelection && selectedAnimals.length > 0
        ? ["All available subgroups"]
        : [];
    const dimensionEntries = [primaryChoice, secondaryChoice].map(
      (choice) => {
        if (choice.dimension === "antibiotics") {
          const antibioticValues = selectedAntibiotics.map(
            (value) => formatAntibioticTagLabel(value) ?? value
          );
          return { label: choice.label, values: antibioticValues };
        }
        return { label: choice.label, values: selectedAnimals };
      }
    );
    return [
      ...dimensionEntries,
      { label: "Dispensing Form", values: selectedForms },
      { label: "Ordination Groups", values: selectedOrdinationGroups },
      { label: "Age Groups", values: subgroupValues },
    ];
  }, [
    selectedAntibiotics,
    selectedAnimals,
    selectedForms,
    selectedOrdinationGroups,
    selectedAnimalSubgroups,
    formatAntibioticTagLabel,
    allowSubgroupSelection,
    primaryChoice,
    secondaryChoice,
  ]);
  const exportDisabled = loading || !hasData;
  const exportDisabledReason = loading
    ? "The graph is updating - please wait."
    : "Generate a graph before exporting.";

  const handleSelectAntibiotics = (newValue: string[] | undefined) => {
    setSelectedAntibiotics((newValue ?? []).slice(0, antibioticLimit));
  };

  const handleSelectAnimals = (newValue: string[] | undefined) => {
    setSelectedAnimals((newValue ?? []).slice(0, animalLimit));
  };

  const handleSelectForms = (newValue: string[] | undefined) => {
    setSelectedForms((newValue ?? []).slice(0, formLimit));
  };

  const handleSelectOrdinationGroups = (newValue: string[] | undefined) => {
    setSelectedOrdinationGroups((newValue ?? []).slice(0, ordinationLimit));
  };

  const handleSelectAnimalSubgroups = (newValue: string[] | undefined) => {
    const limit = subgroupSelectionLimit > 0 ? subgroupSelectionLimit : 0;
    setSelectedAnimalSubgroups((newValue ?? []).slice(0, limit));
  };

  const subgroupOptions = useMemo(
    () => flattenSubgroupOptions(animalSubgroupOptions),
    [animalSubgroupOptions]
  );


  const legendConfigs = useMemo(
    () => buildLegendConfigs(series, resolvedColors),
    [series, resolvedColors]
  );
  const chartBottomMargin = useMemo(
    () => getChartBottomMargin(legendConfigs.length),
    [legendConfigs.length]
  );
  const subgroupDisabled =
    !allowSubgroupSelection ||
    selectedAnimals.length === 0 ||
    animalSubgroupsLoading ||
    !!animalSubgroupsError ||
    subgroupOptions.length === 0;

  const handleToggleAntibioticLock = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      setLockAntibiotics(true);
      setLockAnimals(false);
      setLockForms(false);
      setLockOrdinationGroups(false);
      setLockAnimalSubgroups(false);
    }
  };

  const handleToggleAnimalLock = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      setLockAnimals(true);
      setLockAntibiotics(false);
      setLockForms(false);
      setLockOrdinationGroups(false);
      setLockAnimalSubgroups(false);
    }
  };

  const handleToggleFormLock = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setLockForms(true);
      setLockAntibiotics(false);
      setLockAnimals(false);
      setLockOrdinationGroups(false);
      setLockAnimalSubgroups(false);
    }
  };

  const handleToggleOrdinationGroupLock = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      setLockOrdinationGroups(true);
      setLockAntibiotics(false);
      setLockAnimals(false);
      setLockForms(false);
      setLockAnimalSubgroups(false);
    }
  };

  const handleToggleAnimalSubgroupLock = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      setLockAnimalSubgroups(true);
      setLockAntibiotics(false);
      setLockAnimals(false);
      setLockForms(false);
      setLockOrdinationGroups(false);
    }
  };

  const handleDispensingLevelChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setDispensingLevel(event.target.value as DispensingLevel);
  };
  const lockHandlers: Record<
    VariableDimension,
    (event: React.ChangeEvent<HTMLInputElement>) => void
  > = {
    antibiotics: handleToggleAntibioticLock,
    animals: handleToggleAnimalLock,
  };
  const handlePrimaryVariableChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const nextValue = String(event.target.value);
    const nextOption = getVariableOption(nextValue);
    const previousDimension = primaryChoice.dimension;
    const previousLockState = getDimensionLockState(previousDimension);
    if (nextOption && nextOption.dimension === secondaryChoice.dimension) {
      if (getDimensionLockState(primaryChoice.dimension)) {
        setSecondaryVariableKey(primaryChoice.value);
      } else {
        setSecondaryVariableKey((current) =>
          current === primaryChoice.value ? DEFAULT_VARIABLE_BY_DIMENSION[nextOption.dimension] : current
        );
      }
    }
    if (nextOption && nextOption.dimension !== previousDimension) {
      syncLockStateToDimension(nextOption.dimension, previousLockState);
    }
    setPrimaryVariableKey(nextValue);
  };
  const handleSecondaryVariableChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const nextValue = String(event.target.value);
    const nextOption = getVariableOption(nextValue);
    const previousDimension = secondaryChoice.dimension;
    const previousLockState = getDimensionLockState(previousDimension);
    if (nextOption && nextOption.dimension === primaryChoice.dimension) {
      setPrimaryVariableKey(secondaryChoice.value);
    }
    if (nextOption && nextOption.dimension !== previousDimension) {
      syncLockStateToDimension(nextOption.dimension, previousLockState);
    }
    setSecondaryVariableKey(nextValue);
  };
  const filterSections = [
    {
      id: "primary",
      choice: primaryChoice,
      variableKey: primaryVariableKey,
      onVariableChange: handlePrimaryVariableChange,
      options: primaryOptions,
      helperText: primaryHelperText,
      controlId: primaryControlId,
    },
    {
      id: "secondary",
      choice: secondaryChoice,
      variableKey: secondaryVariableKey,
      onVariableChange: handleSecondaryVariableChange,
      options: secondaryOptions,
      helperText: secondaryHelperText,
      controlId: secondaryControlId,
    },
  ];

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
      setSelectedYearRange(newValue as number[]);
  };

  return (
    <div className="vetstat-container">
      <section className="controls">
        {filterSections.map((section) => {
          const isAntibioticSection = section.choice.dimension === "antibiotics";
          const lockChecked = getDimensionLockState(section.choice.dimension);
          const lockLabel = isAntibioticSection
            ? "Select multiple"
            : "Select multiple";
          const lockHelperText = isAntibioticSection
            ? lockAntibiotics
              ? "Multiple selection enabled. Other categories are restricted to single selection."
              : "Enable to compare up to 10 antibiotics simultaneously."
            : lockAnimals
            ? "Multiple selection enabled. Other categories are restricted to single selection."
            : "Enable to compare up to 10 animal species simultaneously.";
          const sectionValue = isAntibioticSection
            ? selectedAntibiotics
            : selectedAnimals;
          const setSectionValue = isAntibioticSection
            ? handleSelectAntibiotics
            : handleSelectAnimals;
          const sectionLimit = isAntibioticSection
            ? antibioticLimit
            : animalLimit;
          const sectionLoading = isAntibioticSection
            ? antibioticsLoading
            : animalsLoading;
          const sectionError = isAntibioticSection
            ? antibioticsError
            : animalsError;
          const loadingCopy = isAntibioticSection
            ? "Loading list of antibiotics..."
            : "Loading list of animal species...";
          const formatOptionLabelProp = isAntibioticSection
            ? renderAntibioticOptionLabel
            : undefined;
          const formatTagLabelProp = isAntibioticSection
            ? formatAntibioticTagLabel
            : undefined;
          const isOptionGroupProp = isAntibioticSection
            ? isAntibioticGroupOption
            : undefined;
          const lockDisabled = lockChecked;
          const lockHandler = lockHandlers[section.choice.dimension];

          return (
            <FilterSection
              key={section.id}
              id={section.id}
              choice={section.choice}
              variableKey={section.variableKey}
              variableChoices={VARIABLE_OPTIONS}
              onVariableChange={section.onVariableChange}
              options={section.options}
              helperText={section.helperText}
              controlId={section.controlId}
              lockLabel={lockLabel}
              lockHelperText={lockHelperText}
              lockChecked={lockChecked}
              lockDisabled={lockDisabled}
              onLockChange={lockHandler}
              value={sectionValue}
              setValue={setSectionValue}
              maxToSelect={sectionLimit}
              loading={sectionLoading}
              error={sectionError}
              loadingCopy={loadingCopy}
              formatOptionLabel={formatOptionLabelProp}
              formatTagLabel={formatTagLabelProp}
              isOptionGroup={isOptionGroupProp}
              showVariableSelect={false}
            />
          );
        })}
        <div className="controlsGroup">
          <div className="controlsGroupHeader">
            <div>
              <h2>Age Groups</h2>
               <p className="helperText">
                {lockAnimalSubgroups
                  ? "Multiple selection enabled. Other categories are restricted to single selection."
                  : "Enable to compare multiple age groups simultaneously."}
              </p>
              <FormControlLabel
                className="lockToggle"
                control={
                  <Checkbox
                    color="primary"
                    checked={lockAnimalSubgroups}
                    onChange={handleToggleAnimalSubgroupLock}
                    disabled={lockAnimalSubgroups}
                  />
                }
                label="Select multiple"
              />
             
            </div>
          </div>
          <SubgroupSelector
            controlId={animalSubgroupsSelectId}
            allowSubgroupSelection={allowSubgroupSelection}
            subgroupHelperText=""
            subgroupDisabled={subgroupDisabled}
            subgroupOptions={subgroupOptions}
            selectedAnimalSubgroups={selectedAnimalSubgroups}
            setSelectedAnimalSubgroups={handleSelectAnimalSubgroups}
            subgroupSelectionLimit={subgroupLimit}
            maxSelectableFallback={MAX_SELECTED_SUBGROUPS}
            formatSubgroupLabel={formatSubgroupLabel}
            animalSubgroupsLoading={animalSubgroupsLoading}
            animalSubgroupsError={animalSubgroupsError}
            selectedAnimalsCount={selectedAnimals.length}
            subgroupDisabledReason={
              selectedAnimals.length === 0
                ? "Select at least one animal species to choose subgroups."
                : selectedAnimals.length > 1
                ? "Subgroups are not available when multiple animal species are selected."
                : ""
            }
          />
        </div>
        <div className="controlsGroup">
          <div className="controlsGroupHeader controlsGroupHeader--split">
            <div>
              
              <h2>Dispensing Form</h2>
              <p className="helperText">
                {lockForms
                  ? "Multiple selection enabled. Other categories are restricted to single selection."
                  : "Enable to compare multiple dispensing forms."}
              </p>
              <FormControlLabel
                className="lockToggle"
                control={
                  <Checkbox
                    color="primary"
                    checked={lockForms}
                    onChange={handleToggleFormLock}
                    disabled={lockForms}
                  />
                }
                label="Select multiple"
              />
              
            </div>
            <TextField
              select
              variant="outlined"
              size="small"
              label="View"
              value={dispensingLevel}
              onChange={handleDispensingLevelChange}
              className="sectionSelect"
            >
              {DISPENSING_LEVEL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <AutoCompleteMultiple
            id={`${instanceId}-forms-select`}
            label="Dispensing Form"
            options={formOptions}
            disabled={formsLoading || !!formsError}
            value={selectedForms}
            setValue={handleSelectForms}
            maxToSelect={formLimit}
          />
          {formsLoading && (
            <p className="helperText">Loading list of dispensing forms...</p>
          )}
          {formsError && (
            <p className="helperText helperText--error">{formsError}</p>
          )}
        </div>
        <div className="controlsGroup">
          <div className="controlsGroupHeader">
            <div>
              <h2>Ordination Groups</h2>
              <p className="helperText">
                {lockOrdinationGroups
                  ? "Multiple selection enabled. Other categories are restricted to single selection."
                  : "Enable to compare multiple ordination groups."}
              </p>
              <FormControlLabel
                className="lockToggle"
                control={
                  <Checkbox
                    color="primary"
                    checked={lockOrdinationGroups}
                    onChange={handleToggleOrdinationGroupLock}
                    disabled={lockOrdinationGroups}
                  />
                }
                label="Select multiple"
              />
              
            </div>
          </div>
          <AutoCompleteMultiple
            id={`${instanceId}-ordination-select`}
            label="Ordination Groups"
            options={ordinationOptions}
            disabled={ordinationLoading || !!ordinationError}
            value={selectedOrdinationGroups}
            setValue={handleSelectOrdinationGroups}
            maxToSelect={ordinationLimit}
          />
          {ordinationLoading && (
            <p className="helperText">Loading list of ordination groups...</p>
          )}
          {ordinationError && (
            <p className="helperText helperText--error">{ordinationError}</p>
          )}
        </div>
        <div className="controlsGroup">
            <YearRangeSlider
                min={minYear}
                max={maxYear}
                value={selectedYearRange}
                onChange={handleSliderChange}
            />
        </div>
      </section>

      <section className="chartSection">
        <div className="chartActions">
          <ExportDialog
            disabled={exportDisabled}
            disabledReason={exportDisabledReason}
            series={series}
            filtersSummary={filtersSummary}
            usageQuery={usageQuery}
            baseURL={baseURL}
          />
        </div>
        {error && <div className="status status--error">{error}</div>}
        {!error && !hasData && (
          <div className="status">No data points available.</div>
        )}
        {!error && hasData && (
          <div
            className={`chartContainer${chartReloading ? " is-reloading" : ""}`}
            style={{ height: 500 }}
          >
            {chartReloading && <div className="chartOverlay">Updating...</div>}
            <UsageLineChart
              data={series}
              colors={resolvedColors}
              legendConfigs={legendConfigs}
              chartBottomMargin={chartBottomMargin}
              yAxisLegend="Kg Active Substance"
            />
          </div>
        )}
      </section>
    </div>
  );
};

export default DashboardInstance;